import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OktaAuth } from '@okta/okta-auth-js';
import { IAppConfig } from '../models/config';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';

export interface AuthConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  scopes?: string[];
  responseType?: string;
  loginUrl?: string;
  pkce?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthConfigService {
  private config: AuthConfig | null = null;
  private oktaAuthInstance: OktaAuth | null = null;
  private configLoaded = false;

  constructor(
    private http: HttpClient,
    private router: Router, // ✅ Inject Router
    @Inject(DOCUMENT) private document: Document
  ) {
    console.log('[AuthConfigService] Constructor called');
  }

  async loadConfig(): Promise<AuthConfig> {
    console.log('[AuthConfigService] loadConfig called, config exists:', !!this.config);
    
    if (this.config) {
      return this.config;
    }

    try {
      const configData = await this.http.get<IAppConfig>('/assets/jsons/config.json').toPromise();
      console.log('[AuthConfigService] Config data received:', configData);
      if (!configData) {
        throw new Error('Failed to load auth config');
      }
      
      this.config = {
        issuer: configData.issuer,
        clientId: configData.clientId,
        redirectUri: window.location.origin + '/auth/callback', // ✅ Changed to /auth/callback
        scopes: configData.scopes || ['openid', 'profile', 'email'],
        loginUrl: configData.loginUrl,
        responseType: 'code', // ✅ Hardcoded to 'code'
        pkce: true
      };

      this.configLoaded = true;
      console.log('[AuthConfigService] Config loaded successfully:', this.config);
      
      return this.config;
      
    } catch (error) {
      console.error('[AuthConfigService] Error loading auth config:', error);
      throw error;
    }
  }

  getConfig(): AuthConfig {
    console.log('[AuthConfigService] getConfig called, configLoaded:', this.configLoaded);
    
    if (!this.config) {
      console.error('[AuthConfigService] Config not available!');
      throw new Error('Auth config not loaded. Call loadConfig() first during APP_INITIALIZER.');
    }
    return this.config;
  }

  // ...existing code...

createOktaAuth(): OktaAuth {
  console.log('[AuthConfigService] createOktaAuth called, oktaAuthInstance exists:', !!this.oktaAuthInstance);
  
  if (this.oktaAuthInstance) {
    return this.oktaAuthInstance;
  }

  const config = this.getConfig();
  
  this.oktaAuthInstance = new OktaAuth({
    issuer: config.issuer,
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    scopes: config.scopes,
    pkce: config.pkce,
    responseType: 'code',
    tokenManager: {
      storage: 'localStorage',
      autoRenew: true,
      secure: window.location.protocol === 'https:',
      storageKey: 'okta-token-storage',
      expireEarlySeconds: 30
    },
    cookies: {
      secure: window.location.protocol === 'https:',
      sameSite: 'lax'
    },
    
    restoreOriginalUri: async (_oktaAuth, originalUri) => {
      console.log('[AuthConfigService] restoreOriginalUri called, originalUri:', originalUri);
      
      const path = originalUri ? 
        originalUri.replace(window.location.origin, '') : 
        '/file-explorer';
      
      console.log('[AuthConfigService] Navigating to path:', path);
      
      this.router.navigateByUrl(path);
    }
  });
  
  // ✅ Add token event listeners for debugging
  this.oktaAuthInstance.tokenManager.on('added', (key, token) => {
    console.log('[TokenManager] Token added:', key, 'expires at:', token.expiresAt);
  });
  
  this.oktaAuthInstance.tokenManager.on('removed', (key) => {
    console.log('[TokenManager] Token removed:', key);
  });
  
  this.oktaAuthInstance.tokenManager.on('error', (error) => {
    console.error('[TokenManager] Error:', error);
  });
  
  console.log('[AuthConfigService] OktaAuth instance created');
  
  return this.oktaAuthInstance;
}
}